<template>
	<div class="row">
		<div
			:class="[
				labelClass, {
					'pointer': !disabled
				}
			]"
			v-show="label !== ''"
			@click.stop.prevent="changeToggle"
			v-if="showInFront"
		>
			<font-awesome-icon
				v-tooltip.top-center="{
					content: `This ${label} has changed.`,
					trigger:'hover click'
				}"
				v-show="useDirtyIcon && isDirty"
				icon="exclamation-circle"
				class="text-warning"
			>
			</font-awesome-icon> <slot name="start">{{ label }}</slot>
		</div>
		<template v-if="!showInFront">
			<font-awesome-icon
				v-tooltip.top-center="{content: `This ${label} has changed.`, trigger:'hover click'}"
				v-show="useDirtyIcon && isDirty"
				icon="exclamation-circle"
				class="text-warning"
			/>
		</template>
		<div :class="[toggleClass, addedToggleClasses ]">
			<toggle-button
				:cssColors="true"
				:class="toggleSwitchClasses"
				:sync="true"
				v-model="toggleValue"
				:disabled="disabled"
				:name="name"
				:ariaLabel="ariaLabel"
				:id="id"
				:labels="labels"
				ref="toggle"
			/>
		</div>
		<div
			:class="[
				labelClass,
				{
					'pointer': !disabled

				},
				addedLabelClasses
			]"
			v-show="label !== ''"
			@click="changeToggle"
			v-if="!showInFront"
		>
			<slot name="end">
				{{ label }}
			</slot>
		</div>
	</div>
</template>

<script>
	import '../../imports/fontAwesome.js';
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import Button from './Button.vue';
	import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';

	export default {
		name: 'SapphireToggle',
		props: {
			label: {
				type: String,
				default: '',
				required: false,
			},
			value: {
				type: Boolean | String | Number,
				required: true
			},
			toggleClasses: {
				type: String | Object,
				required: false
			},
			disabled: {
				type: Boolean,
				default: false,
			},
			toggleSwitchClasses: {
				type: String | Object,
				required: false
			},
			isDirty: {
				type: Boolean,
				required: false
			},
			useDirtyIcon: {
				type: Boolean,
				default: false
			},
			showInFront: {
				type: Boolean,
				default: true
			},
			ariaLabel: {
				type: String,
				default: ''
			},
			name: {
				type: String,
				default: ''
			},
			id: {
				type: String,
				default: ''
			},
			labels: {
				type: Object | String | Boolean,
				required: false
			},
			message: {
				type: String,
				default: ''
			},
			addedToggleClasses: {
				type: String,
				defualt: ''
			},
			addedLabelClasses: {
				type: String,
				default: ''
			}
		},
		computed:{
			toggleClass() {
				if(this.label === '') {
					if(this.useDirtyIcon && this.isDirty) {
						return 'col-xs-12'
					} else {
						return 'col-xs-12 col-md-12';
					}
				} else {
					if(this.useDirtyIcon && this.isDirty) {
						return 'col-xs-4 col-sm-4 col-md-4';
					} else {
						if(this.showInFront) {
							return 'col-xs-4 col-md-4';
						} else {
							return 'col-xs-4 col-md-4';
						}
					}
				}
			},
			labelClass() {
				if(this.label === '') {
					return '';
				} else {
					if(this.useDirtyIcon && this.isDirty) {
						return 'col-xs-8 col-sm-8 col-md-8';
					} else {
						return 'col-xs-8 col-sm-8';
					}
				}
			},
			toggleValue: {
				get() {
					if(typeof this.value === 'string') {
						if(this.value.length > 1) {
							return this.value.toUpperCase() === 'YES';
						}
						if(this.value.length === 1) {
							return this.value.toUpperCase() === 'Y';
						}
						if(this.value.length === 0) {
							return false;
						}
					}
					if(typeof this.value === 'number') {
						return this.value === 1;
					}
					if(typeof this.value === 'boolean') {
						return this.value;
					}
				},
				set(v) {
					if(typeof this.value === 'string') {
						if(this.value.length > 1) {
							v ?	this.$emit('input', 'YES') : this.$emit('input', 'NO');
						}
						if(this.value.length === 1) {
							v ? this.$emit('input', 'Y') : this.$emit('input', 'N');
						}
						if(this.value.length === 0) {
							let val = v ? 'Y' : 'N';
							this.$emit('input', val);
						}
					}
					if(typeof this.value === 'number') {
						v ? this.$emit('input', 1) : this.$emit('input', 0);
					}
					if(typeof this.value === 'boolean') {
						this.$emit("input", v);
					}
				}
			}
		},
		directives: {
			'tooltip': VTooltip,
			'close-popover': VClosePopover,
			'v-popover': VPopover
		},
		components: {
			FontAwesomeIcon,
			'toggle-button': Button
		},
		methods: {
			changeToggle() {
				if(!this.disabled) {
					this.toggleValue = !this.toggleValue;
				}
			}
		},
	}
</script>

<style lang="scss" scoped>
	.vue-js-switch {
		display: inline-block;
		position: relative;
		vertical-align: middle;
		user-select: none;
		font-size: 10px;
		cursor: pointer;

		.v-switch-input {
			opacity: 0;
			position: absolute;
			width: 1px;
			height: 1px;
		}

		.v-switch-label {
			position: absolute;
			top: 0;
			font-weight: 600;
			color: white;
			z-index: 1;

			&.v-left {
				left: 10px;
			}

			&.v-right {
				right: 10px;
			}
		}

		.v-switch-core {
			display: block;
			position: relative;
			box-sizing: border-box;

			outline: 0;
			margin: 0;

			transition: border-color .3s, background-color .3s;
			user-select: none;

			.v-switch-button {
				display: block;
				position: absolute;
				overflow: hidden;

				top: 0;
				left: 0;

				border-radius: 100%;
				background-color: #fff;
				z-index: 2;
			}
		}

		&.disabled {
			pointer-events: none;
			opacity: 0.6;
		}
	}
	.pointer {
		cursor: pointer;
	}
</style>
